import { useEffect, useState } from 'react';

import { appColors } from '@backrs_ui/common-ui';

import {
  CareerQuestion,
  CareerUserResponseAnswer,
  CareerUserResponseSet,
} from '@backrs_ui/grow-ui/src/models/models';

import { Box, Typography, SxProps, Theme } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import { Answer } from '../atoms/Answer';
import { CustomAnswer } from '../atoms/CustomAnswer';
import { FactsPopover } from '../atoms/FactsPopover';
import { ProgressBar } from '../atoms/ProgressBar';

export type QuestionProps = Readonly<{
  currentQuestionIndex?: number;
  question: CareerQuestion;
  totalLengthOfSurvey: number;
  responsesForQuestion?: CareerUserResponseAnswer;
  userResponseSet: CareerUserResponseSet;
  addUserQuestionResponse: (response: CareerUserResponseAnswer) => void;
  isOnSchoolLevelQuestion?: boolean;
  hasSeenFirstQuestionFact?: boolean;
  setHasSeenFirstQuestionFact?: (value: boolean) => void;
}>;

export function Question({
  currentQuestionIndex,
  question,
  totalLengthOfSurvey,
  responsesForQuestion,
  userResponseSet,
  addUserQuestionResponse,
  isOnSchoolLevelQuestion,
  hasSeenFirstQuestionFact,
  setHasSeenFirstQuestionFact,
}: QuestionProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const areThereFacts = !!question.facts && question.facts.length > 0;

  useEffect(() => {
    setTimeout(() => {
      // on the first question of the survey, we want to show the facts popover so the users know it's a clickable element
      if (
        currentQuestionIndex === 1 &&
        !isOnSchoolLevelQuestion &&
        areThereFacts &&
        hasSeenFirstQuestionFact === false
      ) {
        setIsPopoverOpen(true);
      }
    }, 200);
  }, [currentQuestionIndex, isOnSchoolLevelQuestion, areThereFacts, hasSeenFirstQuestionFact]);

  if (!question) {
    return null;
  }

  const { title, question: question_body, options, is_multi_select } = question;

  function handleAddingAnswerToResponse(optionId: number) {
    if (responsesForQuestion) {
      // if the question is multi-select, we need to toggle the option instead of just adding it
      const updatedOptionIds = is_multi_select
        ? responsesForQuestion.career_question_option_ids.includes(optionId)
          ? responsesForQuestion.career_question_option_ids.filter((id) => id !== optionId)
          : [...responsesForQuestion.career_question_option_ids, optionId]
        : [optionId];

      const updatedResponse = {
        ...responsesForQuestion,
        career_question_option_ids: updatedOptionIds,
      };

      addUserQuestionResponse(updatedResponse);

      return;
    }

    const newResponse: CareerUserResponseAnswer = {
      id: uuidv4(),
      career_user_response_set_id: userResponseSet.id,
      career_question_id: question.id,
      career_question_option_ids: [optionId],
    };

    addUserQuestionResponse(newResponse);
  }

  return (
    <Box sx={questionContainerStyle}>
      <ProgressBar
        currentQuestionSequence={currentQuestionIndex || 0}
        totalLengthOfSurvey={totalLengthOfSurvey}
      />
      <Box sx={questionContentContainerStyle}>
        <Typography variant="h6" sx={sequenceNumberStyle}>
          {currentQuestionIndex ? currentQuestionIndex : ''}
        </Typography>
        <Box width="90%">
          <Box sx={titleFactsButtonContainerStyle}>
            <Typography variant="h5" sx={questionTitleStyle}>
              {title}
            </Typography>
            {areThereFacts && (
              <FactsPopover
                facts={[...question.facts]?.sort((a, b) => a.sequence - b.sequence)}
                isPopoverOpen={isPopoverOpen}
                setIsPopoverOpen={(isOpen) => setIsPopoverOpen(isOpen)}
                setHasSeenFirstQuestionFact={setHasSeenFirstQuestionFact}
              />
            )}
          </Box>
          <Typography sx={questionBodyStyle}>{question_body}</Typography>
          {question.is_custom_response && (
            <CustomAnswer
              responsesForQuestion={responsesForQuestion}
              userResponseSet={userResponseSet}
              question={question}
              addUserQuestionResponse={addUserQuestionResponse}
            />
          )}

          {!question.is_custom_response && (
            <Box sx={answersGroupStyle}>
              {options.map((option, index) => {
                return (
                  <Answer
                    key={`${option.answer}-${option.id}`}
                    answer={option.answer}
                    answerIndex={index}
                    isMultiSelect={question.is_multi_select || false}
                    isSelected={
                      responsesForQuestion?.career_question_option_ids.includes(option.id) ?? false
                    }
                    selectAnswer={() => handleAddingAnswerToResponse(option.id)}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const questionBodyStyle: SxProps<Theme> = {
  lineHeight: 1.25,
  fontSize: '16px',
  mb: {
    xs: 2,
    sm: 3,
    xl: 4,
  },
  whiteSpace: 'pre-line',
  '@media (max-width: 656px)': {
    mb: 2,
  },
};

const questionContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: appColors.neutral.white,
  minHeight: '30vh',
  maxHeight: '75vh',
  borderRadius: '1rem',
  border: `2px solid ${appColors.neutral.black}`,
  overflow: 'hidden',
};

const questionTitleStyle: SxProps<Theme> = {
  fontWeight: 700,
  mb: 1,
  lineHeight: 1.15,
  fontSize: {
    xs: '24px',
    sm: '32px',
  },
  pt: {
    xs: '.5rem',
    sm: 'unset',
  },
};

const sequenceNumberStyle: SxProps<Theme> = {
  color: appColors.color.blue1,
  minWidth: {
    xs: '16px',
    sm: '24px',
  },
  textAlign: 'end',
  fontSize: '13pt',
  marginTop: 1,
};

const questionContentContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  paddingLeft: {
    xs: '.5rem',
    sm: '2rem',
  },
  paddingRight: {
    xs: '1rem',
    sm: '2rem',
  },
  paddingBottom: '3rem',
  paddingTop: {
    xs: '.5rem',
    sm: '2rem',
  },
  gap: '1rem',
  '@media (max-width: 656px)': {
    paddingBottom: '4rem',
  },
  overflow: 'hidden',
};

const titleFactsButtonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  '@media (max-width: 656px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0.5,
    mb: 2,
  },
};

const answersGroupStyle: SxProps<Theme> = {
  flexGrow: 1,
  gap: {
    xs: 0.75,
    sm: 1,
    md: 1.25,
  },
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflowY: 'auto',
  paddingRight: '1rem',
  maxHeight: { xs: '33vh', sm: '50vh' },
  // smaller screen height target
  '@media screen and (min-width: 768px) and (max-width: 1366px) and (max-height: 700px)': {
    maxHeight: '33vh',
  },
  // Mobile phone specific styling
  '@media screen and (max-width: 600px)': {
    maxHeight: '30vh',
  },
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: appColors.color.blue6,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: appColors.color.blue1,
    borderRadius: '4px',
  },
};
